import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Loader2, Eye, MousePointerClick, Users, ExternalLink, Filter, ArrowUp,
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar,
} from "recharts";
import { getToken, formatApiError } from "@/lib/adminApi";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHdr = () => ({ Authorization: `Bearer ${getToken()}` });

const RANGES = [
  { value: 7, label: "7 giorni" },
  { value: 30, label: "30 giorni" },
  { value: 90, label: "90 giorni" },
];

export default function AnalyticsTab() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/analytics/overview?days=${days}`, { headers: authHdr() });
      setData(data);
    } catch (err) { setError(formatApiError(err)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  return (
    <div data-testid="admin-analytics-tab" className="space-y-8">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-serif-display text-3xl text-[#0B192C]">Analytics</h2>
        <div className="flex items-center gap-2">
          {RANGES.map((r) => (
            <button
              key={r.value}
              onClick={() => setDays(r.value)}
              data-testid={`range-${r.value}`}
              className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] border transition-colors ${
                days === r.value
                  ? "bg-[#0B192C] border-[#0B192C] text-white"
                  : "border-[#E5E1DA] text-[#0B192C] hover:border-[#0B192C]"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {loading && !data ? (
        <div className="flex items-center gap-3 text-[#3A4F6B]"><Loader2 className="animate-spin" size={16} /> Caricamento...</div>
      ) : error ? (
        <p className="text-sm text-red-700">{error}</p>
      ) : !data ? null : (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <KpiCard icon={Eye} label="Pageviews" value={data.totals.pageviews} accent="#0E7C8F" testid="kpi-pageviews" />
            <KpiCard icon={Users} label="Visitatori unici" value={data.totals.unique_visitors} accent="#E89B6C" testid="kpi-uniques" />
            <KpiCard icon={MousePointerClick} label="Click tracciati" value={data.totals.clicks} accent="#1E3557" testid="kpi-clicks" />
          </div>

          {/* Time series */}
          <div className="bg-white border border-[#E5E1DA] p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif-display text-xl text-[#0B192C]">Andamento visite</h3>
              <span className="text-[10px] uppercase tracking-[0.18em] text-[#3A4F6B]">Ultimi {days} giorni</span>
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={data.series} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E1DA" />
                <XAxis dataKey="date" stroke="#3A4F6B" fontSize={10} />
                <YAxis stroke="#3A4F6B" fontSize={10} />
                <Tooltip
                  contentStyle={{ background: "#0B192C", border: "1px solid #E89B6C", color: "#F9F8F6", fontSize: "12px" }}
                  labelStyle={{ color: "#E89B6C" }}
                />
                <Line type="monotone" dataKey="visits" stroke="#0E7C8F" strokeWidth={2} dot={false} name="Pageviews" />
                <Line type="monotone" dataKey="uniques" stroke="#E89B6C" strokeWidth={2} dot={false} name="Unici" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Top pages + sources */}
          <div className="grid lg:grid-cols-2 gap-6">
            <TopList
              title="Pagine più viste"
              items={data.top_pages.map(p => ({ key: p.path || "/", main: p.path || "/", value: p.visits, sub: `${p.uniques} unici` }))}
              testid="top-pages-list"
            />
            <TopList
              title="Sorgenti di traffico"
              items={data.sources.map(s => ({ key: s.source, main: s.source || "direct", value: s.visits, sub: `${s.uniques} unici` }))}
              testid="top-sources-list"
            />
          </div>

          {/* Top clicks chart */}
          <div className="bg-white border border-[#E5E1DA] p-6">
            <h3 className="font-serif-display text-xl text-[#0B192C] mb-4">Click sui CTA principali</h3>
            {data.top_clicks.length === 0 ? (
              <p className="text-sm text-[#3A4F6B] py-8 text-center">
                Nessun click tracciato ancora. Aggiungi <code className="font-mono bg-[#F4E8D6]/40 px-1">data-track=&quot;label&quot;</code> agli elementi della landing per vederli qui.
              </p>
            ) : (
              <ResponsiveContainer width="100%" height={Math.max(180, data.top_clicks.length * 28)}>
                <BarChart data={data.top_clicks} layout="vertical" margin={{ left: 100 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E1DA" />
                  <XAxis type="number" stroke="#3A4F6B" fontSize={10} />
                  <YAxis dataKey="label" type="category" stroke="#3A4F6B" fontSize={10} width={140} />
                  <Tooltip
                    contentStyle={{ background: "#0B192C", border: "1px solid #E89B6C", color: "#F9F8F6", fontSize: "12px" }}
                    labelStyle={{ color: "#E89B6C" }}
                  />
                  <Bar dataKey="n" fill="#E89B6C" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, accent, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-[#E5E1DA] p-5 flex items-start justify-between">
      <div>
        <p className="overline" style={{ color: accent }}>{label}</p>
        <p className="font-serif-display text-4xl mt-2 text-[#0B192C]">{value?.toLocaleString("it-IT") ?? 0}</p>
      </div>
      <Icon size={28} strokeWidth={1.3} style={{ color: accent }} />
    </div>
  );
}

function TopList({ title, items, testid }) {
  if (!items?.length) {
    return (
      <div className="bg-white border border-[#E5E1DA] p-6">
        <h3 className="font-serif-display text-xl text-[#0B192C] mb-4">{title}</h3>
        <p className="text-sm text-[#3A4F6B] py-8 text-center">Nessun dato ancora.</p>
      </div>
    );
  }
  const max = Math.max(...items.map(i => i.value), 1);
  return (
    <div data-testid={testid} className="bg-white border border-[#E5E1DA] p-6">
      <h3 className="font-serif-display text-xl text-[#0B192C] mb-4">{title}</h3>
      <ul className="space-y-2">
        {items.slice(0, 8).map((it, i) => (
          <li key={it.key + i} className="space-y-1">
            <div className="flex items-center justify-between text-xs">
              <span className="font-mono truncate flex-1 mr-3" title={it.main}>{it.main}</span>
              <span className="text-[#0B192C] font-bold">{it.value}</span>
            </div>
            <div className="h-1 bg-[#E5E1DA]">
              <div className="h-full bg-[#0E7C8F]" style={{ width: `${(it.value / max) * 100}%` }} />
            </div>
            <p className="text-[10px] text-[#3A4F6B]">{it.sub}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}
